/**
 * Arrows — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_motifs_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const arrowsPack: ToilePack = {
    slug: 'arrows',
    category: 'motif',
    name: 'Arrows',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'feathered', label: 'Feathered arrow', subject: 'A single arrow with a flint head, a wooden shaft and three feathers at the tail, drawn horizontally across a plain ground, a small tribal wrap near the head.'},
        {key: 'set', label: 'Hand-drawn set', subject: 'A set of twelve hand-drawn arrows — straight, curved, looped, double-headed, dashed, a swoosh — arranged in a grid on a plain ground.'},
        {key: 'target', label: 'Arrow in target', subject: 'A round straw target with concentric rings and one arrow in the centre, two more in the outer rings, a plain ground.'},
    ],
    styles: [
        {key: 'boho', label: 'Boho', style: 'boho print, terracotta and cream, textured paper, hand-drawn', palette: 'warm terracotta and cream, no other colour', framing: 'set on a cream textured background with a hand-drawn border'},
        {key: 'flat', label: 'Flat', style: 'flat vector illustration, bold simplified shapes, limited palette, plain ground', palette: 'a single bold colour on white, no gradients', framing: 'centred on a plain white background with generous padding around the shape'},
        {key: 'tattoo', label: 'Tattoo linework', style: 'fine-line tattoo design, black ink only, clean weighted outlines, dotwork shading, white ground', palette: 'black ink only, no other colour', framing: 'centred on a plain white background with a clear margin around the design'},
    ],
    directions: ['as a heart shape', 'pointing every direction', 'for a nursery print'],
    directionHint: 'try: as a heart shape',
    seo: {
        title: 'Arrow art: feathered, hand-drawn set, target',
        metaDescription: 'Nine arrow illustrations — a feathered arrow, a set of twelve hand-drawn arrows and a target — in boho, flat and tattoo linework styles.',
        h1: 'Arrow drawings: feathered, hand-drawn set, target in three styles',
        tagline: 'Nine renders, three arrow readings, three looks each — boho to tattoo.',
        intro: 'The shape that decorates a nursery wall and a slide deck alike: a single feathered arrow, a grid of twelve hand-drawn direction arrows, and an arrow in a straw target. Each renders in a hand-drawn boho print, flat bold shapes, or fine tattoo linework. Ask for a heart-shaped arrow set or an all-directions version and the same three looks carry it.',
        useCases: [
            'a nursery or wall print',
            'a tattoo flash reference sheet',
            'a presentation or icon set',
            'a social media graphic',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each is composed in a wide landscape crop, suited to a print, a card or an icon sheet.'},
            {q: 'Can I get a heart-shaped arrow or all-directions set?', a: 'Yes — the direction field takes any variation, and the same three styles render it.'},
            {q: "What's the difference between the three styles?", a: 'Boho is terracotta and cream on textured paper, flat is a single bold colour on white, and tattoo linework is black-ink dotwork shading.'},
            {q: 'Is this a real photograph of an arrow?', a: 'No — every image is an illustration, drawn in the chosen style, not a photo.'},
        ],
    },
}
